import React, { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import * as ReactBootstrap from 'react-bootstrap'
import { RenderField } from '../../components/forms/HookFormFields'
import { titleCase, lowerCase } from '../../components/common/formatters'
import { User } from '../../api'

interface UserProfileFormProps {
  onSubmit: (values: Partial<User>) => void
  initialValues?: Partial<User>
}

interface UserProfileFormData {
  first_name: string
  last_name: string
  contact_number: string
  city: string
  pin_code: string
  gsx_ship_to: string
  gsx_user_name: string
  gsx_technician_id: string
}

const userProfileSchema = yup.object().shape({
  first_name: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim()),
  last_name: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim()),
  contact_number: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim())
    .test(
      'contactNumber',
      'Invalid Contact number',
      (value: string | undefined) => {
        if (!value) return true
        return /^[0-9,]+$/i.test(value)
      }
    ),
  city: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim()),
  pin_code: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim()),
  gsx_ship_to: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim())
    .test(
      'minLength',
      'Must be 10 characters or more',
      (value: string | undefined) => {
        if (!value) return true
        return value.length >= 10
      }
    ),
  gsx_user_name: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim()),
  gsx_technician_id: yup
    .string()
    .test('required', 'Required', (value: string | undefined) => !!value?.trim()),
})

export const UserProfileForm: React.FC<UserProfileFormProps> = ({
  onSubmit,
  initialValues,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<UserProfileFormData>({
    resolver: yupResolver(userProfileSchema),
    defaultValues: {
      first_name: initialValues?.first_name || '',
      last_name: initialValues?.last_name || '',
      contact_number: initialValues?.contact_number || '',
      city: initialValues?.city || '',
      pin_code: initialValues?.pin_code || '',
      gsx_ship_to: initialValues?.gsx_ship_to || '',
      gsx_user_name: initialValues?.gsx_user_name || '',
      gsx_technician_id: initialValues?.gsx_technician_id || '',
    },
  })

  // Reset form only when initialValues first loads or changes significantly
  useEffect(() => {
    if (initialValues) {
      reset({
        first_name: initialValues.first_name || '',
        last_name: initialValues.last_name || '',
        contact_number: initialValues.contact_number || '',
        city: initialValues.city || '',
        pin_code: initialValues.pin_code || '',
        gsx_ship_to: initialValues.gsx_ship_to || '',
        gsx_user_name: initialValues.gsx_user_name || '',
        gsx_technician_id: initialValues.gsx_technician_id || '',
      }, { keepDirty: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.id]) // Only reset when user ID changes (new user loaded)


  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ReactBootstrap.Row>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <Controller
            name="first_name"
            control={control}
            render={({ field }) => {
              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const normalized = titleCase(e.target.value)
                field.onChange(normalized || '')
              }
              return (
                <RenderField
                  label="First Name"
                  type="text"
                  error={errors.first_name?.message}
                  controllerProps={field}
                  onChange={handleChange}
                />
              )
            }}
          />
        </ReactBootstrap.Col>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <Controller
            name="last_name"
            control={control}
            render={({ field }) => {
              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const normalized = titleCase(e.target.value)
                field.onChange(normalized || '')
              }
              return (
                <RenderField
                  label="Last Name"
                  type="text"
                  error={errors.last_name?.message}
                  controllerProps={field}
                  onChange={handleChange}
                />
              )
            }}
          />
        </ReactBootstrap.Col>
      </ReactBootstrap.Row>
      <ReactBootstrap.Row>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <RenderField
            label="Contact Number"
            type="text"
            error={errors.contact_number?.message}
            registerProps={register('contact_number')}
          />
        </ReactBootstrap.Col>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <RenderField
            label="City"
            type="text"
            error={errors.city?.message}
            registerProps={register('city')}
          />
        </ReactBootstrap.Col>
      </ReactBootstrap.Row>
      <ReactBootstrap.Row>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <RenderField
            label="PIN Code"
            type="text"
            error={errors.pin_code?.message}
            registerProps={register('pin_code')}
          />
        </ReactBootstrap.Col>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <RenderField
            label="GSX Ship To"
            type="text"
            error={errors.gsx_ship_to?.message}
            registerProps={register('gsx_ship_to')}
          />
        </ReactBootstrap.Col>
      </ReactBootstrap.Row>
      <ReactBootstrap.Row>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <Controller
            name="gsx_user_name"
            control={control}
            render={({ field }) => {
              const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const normalized = lowerCase(e.target.value)
                field.onChange(normalized || '')
              }
              return (
                <RenderField
                  label="GSX User Name"
                  type="text"
                  error={errors.gsx_user_name?.message}
                  controllerProps={field}
                  onChange={handleChange}
                />
              )
            }}
          />
        </ReactBootstrap.Col>
        <ReactBootstrap.Col md={6} xs={12} lg={6}>
          <RenderField
            label="GSX Technician ID"
            type="text"
            error={errors.gsx_technician_id?.message}
            registerProps={register('gsx_technician_id')}
          />
        </ReactBootstrap.Col>
      </ReactBootstrap.Row>
      <ReactBootstrap.Row></ReactBootstrap.Row>
      <div style={{ display: 'flow-root' }}>
        <br />
        <ReactBootstrap.Button
          style={{ paddingLeft: '0px' }}
          variant="link"
          className="float-left btn text-dark btn-sm"
          type="button"
          disabled={!isDirty || isSubmitting}
          onClick={() => reset()}
        >
          Clear
        </ReactBootstrap.Button>
        <ReactBootstrap.Button
          className={'float-end'}
          variant="dark"
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Hold' : 'Submit'}
        </ReactBootstrap.Button>
      </div>
    </form>
  )
}

export default UserProfileForm
